import { useState, useRef, useEffect } from 'react';
import { X, Minus, GripVertical, Search, MapPin, Calendar, Briefcase, ArrowLeft, Copy, FileText, Building2, DollarSign, Bookmark, Sparkles } from 'lucide-react';
import { useJobSearchPanel } from '@/contexts/JobSearchPanelContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import axios from 'axios';
import { supabase } from '@/integrations/supabase/client';
import { useSubscription } from '@/hooks/useSubscription';
import { useSmartApply } from '@/hooks/useSmartApply';
import { useExtensionQueue } from '@/hooks/useExtensionQueue';
import { useNavigate } from 'react-router-dom';

const RAPIDAPI_KEY = 'e4f5a1aee4mshc07444184d98983p107903jsn756fee66cf44';
const RAPIDAPI_HOST = 'jsearch.p.rapidapi.com';

interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  description: string;
  posted_date: string;
  job_type?: string;
  salary?: string;
  remote?: boolean;
  logo?: string;
  url?: string;
  saved?: boolean;
  source?: string;
}

export function JobSearchPanel() {
  const { isOpen, isMinimized, closePanel, toggleMinimize, autoFillJob } = useJobSearchPanel();
  const { hasFeature } = useSubscription();
  const { addToSmartApply } = useSmartApply();
  const { addToExtensionQueue, loading: extensionLoading } = useExtensionQueue();
  const navigate = useNavigate();
  const [position, setPosition] = useState({ x: 20, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [location, setLocation] = useState('');
  const [jobType, setJobType] = useState('all');
  const [datePosted, setDatePosted] = useState('all');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [savedJobIds, setSavedJobIds] = useState<Set<string>>(new Set());
  const [jobNotes, setJobNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadSavedJobs();
  }, []);

  const loadSavedJobs = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data } = await supabase
      .from("saved_jobs")
      .select("job_id")
      .eq("user_id", user.id);

    if (data) {
      setSavedJobIds(new Set(data.map(j => j.job_id)));
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.drag-handle')) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart]);

  const formatLocation = (job: any) => {
    const parts = [];
    if (job.job_city) parts.push(job.job_city);
    if (job.job_state) parts.push(job.job_state);
    if (job.job_country && parts.length === 0) parts.push(job.job_country);
    return parts.length > 0 ? parts.join(', ') : 'Remote';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Recently';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
      if (diffInDays === 0) return 'Today';
      if (diffInDays === 1) return 'Yesterday';
      if (diffInDays < 7) return `${diffInDays}d ago`;
      if (diffInDays < 30) return `${Math.floor(diffInDays / 7)}w ago`;
      return `${Math.floor(diffInDays / 30)}mo ago`;
    } catch {
      return 'Recently';
    }
  };

  const formatSalary = (job: any) => {
    const min = job.job_min_salary;
    const max = job.job_max_salary;
    const currency = job.job_salary_currency || 'USD';
    if (!min && !max) return null;
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0,
    });
    if (min && max) return `${formatter.format(min)}-${formatter.format(max)}`;
    if (min) return `From ${formatter.format(min)}`;
    if (max) return `Up to ${formatter.format(max)}`;
    return null;
  };

  const formatJobType = (type: string) => {
    if (!type) return 'Full-time';
    return type.split('_').map(word => word.charAt(0) + word.slice(1).toLowerCase()).join('-');
  };

  const detectSourceFromUrl = (url: string): string => {
    if (!url || url === '#') return 'direct';
    
    const urlLower = url.toLowerCase();
    
    if (urlLower.includes('linkedin.com')) return 'linkedin';
    if (urlLower.includes('indeed.com')) return 'indeed';
    if (urlLower.includes('glassdoor.com')) return 'glassdoor';
    if (urlLower.includes('ziprecruiter.com')) return 'ziprecruiter';
    if (urlLower.includes('greenhouse.io') || urlLower.includes('greenhouse')) return 'greenhouse';
    if (urlLower.includes('lever.co')) return 'lever';
    if (urlLower.includes('workday.com')) return 'workday';
    if (urlLower.includes('smartrecruiters.com')) return 'smartrecruiters';
    if (urlLower.includes('monster.com')) return 'monster';
    if (urlLower.includes('careerbuilder.com')) return 'careerbuilder';
    
    return 'company';
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter job keywords');
      return;
    }

    setIsLoading(true);
    const query = location ? `${searchQuery} in ${location}` : searchQuery;

    try {
      const response = await axios.get(
        'https://jsearch.p.rapidapi.com/search',
        {
          params: {
            query: query,
            page: '1',
            num_pages: '1',
            date_posted: datePosted !== 'all' ? datePosted : 'all',
          },
          headers: {
            'x-rapidapi-key': RAPIDAPI_KEY,
            'x-rapidapi-host': RAPIDAPI_HOST,
          },
          timeout: 15000,
        }
      );

      const jobsData = response.data?.data || [];
      
      const transformedJobs = jobsData.map((job: any) => {
        const jobUrl = job.job_apply_link || job.job_google_link || '#';
        
        return {
          id: job.job_id || `job_${Date.now()}_${Math.random()}`,
          title: job.job_title || 'No Title',
          company: job.employer_name || 'Unknown Company',
          location: formatLocation(job),
          description: job.job_description || '',
          url: jobUrl,
          posted_date: formatDate(job.job_posted_at_datetime_utc),
          salary: formatSalary(job),
          job_type: formatJobType(job.job_employment_type),
          remote: job.job_is_remote || false,
          logo: job.employer_logo || null,
          saved: savedJobIds.has(job.job_id || ''),
          source: detectSourceFromUrl(jobUrl),
        };
      });

      setJobs(transformedJobs);
      setSelectedJob(null);
      
      if (transformedJobs.length === 0) {
        toast.info('No jobs found. Try different keywords.');
      } else {
        toast.success(`Found ${transformedJobs.length} jobs!`);
      }
    } catch (error: any) {
      console.error('Job search error:', error);
      if (error.response?.status === 403) {
        toast.error('API authentication failed');
      } else if (error.response?.status === 429) {
        toast.error('Rate limit reached. Try again later.');
      } else {
        toast.error('Failed to search jobs. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseJob = (job: Job) => {
    // Use window.location.pathname instead of useLocation hook to avoid Router context issues
    const pathname = window.location.pathname;
    
    // Store job data in sessionStorage for the target page to consume
    const jobData = {
      title: job.title,
      company: job.company,
      location: job.location,
      description: job.description,
    };
    
    // Store based on which page user is on
    if (pathname.includes('/letters')) {
      // For cover letter page - set job text directly
      sessionStorage.setItem('scannedJob', JSON.stringify(jobData));
      // Trigger a custom event to notify the page
      window.dispatchEvent(new CustomEvent('jobDataUpdated', { detail: jobData }));
      toast.success(`Job loaded: ${job.title} at ${job.company}`);
    } else if (pathname.includes('/ats-score-checker')) {
      // For ATS page - set job description
      sessionStorage.setItem('scannedJob', JSON.stringify(jobData));
      window.dispatchEvent(new CustomEvent('jobDataUpdated', { detail: jobData }));
      toast.success(`Job loaded: ${job.title} at ${job.company}`);
    } else if (pathname.includes('/job-analyzer')) {
      // For job analyzer page
      sessionStorage.setItem('scannedJob', JSON.stringify(jobData));
      window.dispatchEvent(new CustomEvent('jobDataUpdated', { detail: jobData }));
      toast.success(`Job loaded: ${job.title} at ${job.company}`);
    } else if (pathname.includes('/smart-apply')) {
      // For smart apply page
      sessionStorage.setItem('scannedJob', JSON.stringify(jobData));
      window.dispatchEvent(new CustomEvent('jobDataUpdated', { detail: jobData }));
      toast.success(`Job loaded: ${job.title} at ${job.company}`);
    } else {
      // Fallback to old behavior
      autoFillJob(job.description);
      toast.success(`${job.title} at ${job.company} has been added.`);
    }
  };

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Job description copied to clipboard.');
  };

  const handleSaveJob = async (job: Job) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error("Please sign in to save jobs");
      return;
    }

    setIsSaving(true);
    try {
      if (job.saved) {
        await supabase
          .from("saved_jobs")
          .delete()
          .eq("user_id", user.id)
          .eq("job_id", job.id);
        
        setSavedJobIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(job.id);
          return newSet;
        });
        
        setJobs(jobs.map(j => j.id === job.id ? { ...j, saved: false } : j));
        if (selectedJob?.id === job.id) {
          setSelectedJob({ ...selectedJob, saved: false });
        }
        toast.success("Job removed from saved");
      } else {
        const { error } = await supabase.from("saved_jobs").insert({
          user_id: user.id,
          job_id: job.id,
          title: job.title,
          company: job.company,
          location: job.location,
          posted_date: job.posted_date,
          job_type: job.job_type || 'Full-time',
          remote: job.remote ? 'remote' : 'on-site',
          description: job.description || "",
          url: job.url || '',
          salary: job.salary,
          logo: job.logo,
          notes: jobNotes || null,
        });

        if (error) throw error;
        
        setSavedJobIds(prev => new Set(prev).add(job.id));
        setJobs(jobs.map(j => j.id === job.id ? { ...j, saved: true } : j));
        if (selectedJob?.id === job.id) {
          setSelectedJob({ ...selectedJob, saved: true });
        }
        setJobNotes('');
        toast.success("Job saved to Application Tracker!");
      }
    } catch (error: any) {
      console.error("Error saving job:", error);
      if (error.code === '23505') {
        toast.error("Job already saved");
      } else {
        toast.error("Failed to save job");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSmartApply = async (job: Job) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error('Please sign in to use Smart Apply');
      return;
    }

    if (!hasFeature('smartApply')) {
      toast.error('Smart Apply is a Premium feature');
      navigate('/pricing');
      return;
    }

    const added = await addToSmartApply({
      job_id: job.id,
      title: job.title,
      company: job.company,
      location: job.location,
      description: job.description,
      url: job.url || '',
      salary: job.salary,
      remote: job.remote ? 'remote' : 'on-site',
      job_type: job.job_type || 'Full-time',
      logo: job.logo,
    });

    if (added) {
      toast.success('Added to Smart Apply Queue!');
      navigate('/app/smart-apply/queue');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={panelRef}
      className="fixed z-[1000] transition-all duration-300"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: isMinimized ? '60px' : '400px',
      }}
      onMouseDown={handleMouseDown}
    >
      <div className="backdrop-blur-lg bg-slate-900/90 rounded-lg shadow-2xl border border-primary/20">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-border/50 drag-handle cursor-move">
          <div className="flex items-center gap-2">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
            {!isMinimized && <span className="font-semibold text-sm">Job Search</span>}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={toggleMinimize}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={closePanel}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        {!isMinimized && (
          <div className="p-4">
            {!selectedJob ? (
              <>
                {/* Search Section */}
                <div className="space-y-3 mb-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Search job titles, companies..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      className="flex-1"
                    />
                    <Button onClick={handleSearch} size="icon" disabled={isLoading}>
                      <Search className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Location"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      className="text-xs"
                    />
                    <Select value={datePosted} onValueChange={setDatePosted}>
                      <SelectTrigger className="text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Any Time</SelectItem>
                        <SelectItem value="today">Today</SelectItem>
                        <SelectItem value="3days">Last 3 days</SelectItem>
                        <SelectItem value="week">Last week</SelectItem>
                        <SelectItem value="month">Last month</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Results List */}
                <ScrollArea className="h-[60vh]">
                  {isLoading ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="p-3 rounded-lg border border-border">
                          <Skeleton className="h-4 w-3/4 mb-2" />
                          <Skeleton className="h-3 w-1/2 mb-2" />
                          <Skeleton className="h-3 w-full" />
                        </div>
                      ))}
                    </div>
                  ) : jobs.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Search className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">No jobs found</p>
                      <p className="text-xs mt-1">Try adjusting your search criteria</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {jobs.map((job) => (
                        <div
                          key={job.id}
                          className="p-2.5 rounded-lg border border-border hover:border-primary/50 transition-colors group relative overflow-hidden"
                        >
                          {/* Source Badge */}
                          {job.source && job.source !== 'direct' && (
                            <Badge 
                              variant="secondary"
                              className={`absolute top-1.5 right-1.5 text-[8px] px-1 py-0.5 font-medium ${
                                job.source === 'linkedin' ? 'bg-blue-600/90 text-white' :
                                job.source === 'indeed' ? 'bg-purple-600/90 text-white' :
                                job.source === 'glassdoor' ? 'bg-green-600/90 text-white' :
                                job.source === 'ziprecruiter' ? 'bg-green-500/90 text-white' :
                                job.source === 'greenhouse' ? 'bg-teal-600/90 text-white' :
                                job.source === 'lever' ? 'bg-indigo-600/90 text-white' :
                                'bg-slate-600/90 text-white'
                              }`}
                            >
                              {job.source === 'linkedin' ? 'LinkedIn' :
                               job.source === 'indeed' ? 'Indeed' :
                               job.source === 'glassdoor' ? 'Glassdoor' :
                               job.source === 'ziprecruiter' ? 'ZipRecruiter' :
                               job.source === 'greenhouse' ? 'Greenhouse' :
                               job.source === 'lever' ? 'Lever' :
                               job.source === 'company' ? 'Direct' :
                               job.source}
                            </Badge>
                          )}
                          
                          <div onClick={() => setSelectedJob(job)} className="cursor-pointer">
                            {job.logo && (
                              <img src={job.logo} alt={job.company} className="w-7 h-7 rounded mb-1.5 object-contain bg-background" />
                            )}
                            <h3 className="font-medium text-sm leading-tight mb-1 group-hover:text-primary transition-colors line-clamp-2 pr-14">
                              {job.title}
                            </h3>
                            <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              {job.company}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2 flex-wrap">
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {job.location}
                              </span>
                              {job.remote && (
                                <Badge variant="secondary" className="text-xs px-1 py-0">Remote</Badge>
                              )}
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {job.posted_date}
                              </span>
                            </div>
                            {job.salary && (
                              <p className="text-xs text-primary mb-2 flex items-center gap-1">
                                <DollarSign className="h-3 w-3" />
                                {job.salary}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground line-clamp-2">
                              {job.description.substring(0, 100)}...
                            </p>
                          </div>
                          
                          {/* Action Buttons */}
                          <TooltipProvider>
                            <div className="absolute top-3 right-3 flex gap-1">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 hover:bg-background/80"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSaveJob(job);
                                    }}
                                    disabled={isSaving}
                                  >
                                    <Bookmark className={`h-3 w-3 ${job.saved ? 'fill-current' : ''}`} />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">
                                  {job.saved ? 'Remove from Tracker' : 'Save to Tracker'}
                                </TooltipContent>
                              </Tooltip>
                              
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 hover:bg-background/80"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSmartApply(job);
                                    }}
                                  >
                                    <Sparkles className="h-3 w-3" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">
                                  Smart Apply
                                </TooltipContent>
                              </Tooltip>
                              
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 hover:bg-background/80"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleUseJob(job);
                                    }}
                                  >
                                    <FileText className="h-3 w-3" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">
                                  Auto-fill
                                </TooltipContent>
                              </Tooltip>
                              
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 hover:bg-background/80"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCopyToClipboard(job.description);
                                    }}
                                  >
                                    <Copy className="h-3 w-3" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">
                                  Copy Description
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </TooltipProvider>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </>
            ) : (
              <>
                {/* Job Detail View */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="mb-3"
                  onClick={() => setSelectedJob(null)}
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back to Results
                </Button>

                <ScrollArea className="h-[60vh]">
                  <div className="space-y-4">
                    <div>
                      <h2 className="font-semibold text-lg mb-1">{selectedJob.title}</h2>
                      <p className="text-sm text-muted-foreground mb-2">{selectedJob.company}</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mb-4">
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {selectedJob.location}
                        </span>
                        {selectedJob.job_type && (
                          <span className="flex items-center gap-1">
                            <Briefcase className="h-3 w-3" />
                            {selectedJob.job_type}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {selectedJob.posted_date}
                        </span>
                      </div>
                    </div>

                    <div className="prose prose-sm prose-invert max-w-none">
                      <p className="text-sm whitespace-pre-wrap">{selectedJob.description}</p>
                    </div>

                    {/* Notes section for saving */}
                    {!selectedJob.saved && (
                      <div className="space-y-2 pt-4 border-t border-border">
                        <label className="text-xs font-medium">Add Notes (Optional)</label>
                        <Textarea
                          placeholder="Add notes about this job, application status, or reminders..."
                          value={jobNotes}
                          onChange={(e) => setJobNotes(e.target.value)}
                          className="h-20 text-xs"
                        />
                      </div>
                    )}

                    <div className="space-y-2 pt-4 border-t border-border">
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => handleSaveJob(selectedJob)}
                        disabled={isSaving}
                      >
                        <Bookmark className={`h-4 w-4 mr-2 ${selectedJob.saved ? 'fill-current' : ''}`} />
                        {selectedJob.saved ? 'Remove from' : 'Save to'} Application Tracker
                      </Button>
                      
                      <Button
                        className="w-full"
                        onClick={() => handleSmartApply(selectedJob)}
                      >
                        <Sparkles className="h-4 w-4 mr-2" />
                        Send to Smart Apply
                      </Button>

                      <Button
                        variant="outline"
                        className="w-full border-primary/50 hover:bg-primary/10"
                        onClick={async () => {
                          if (!selectedJob.url) {
                            toast.error('Job URL is required for auto-apply');
                            return;
                          }
                          await addToExtensionQueue({
                            job_id: selectedJob.id,
                            title: selectedJob.title,
                            company: selectedJob.company,
                            location: selectedJob.location,
                            description: selectedJob.description,
                            url: selectedJob.url,
                            salary: selectedJob.salary,
                            remote: selectedJob.remote ? 'remote' : 'on-site',
                            job_type: selectedJob.job_type || 'Full-time',
                            logo: selectedJob.logo,
                          });
                        }}
                        disabled={extensionLoading || !selectedJob.url}
                      >
                        <Building2 className="h-4 w-4 mr-2" />
                        Apply via Extension
                      </Button>

                      <div className="flex gap-2">
                        <Button
                          variant="secondary"
                          className="flex-1"
                          onClick={() => handleUseJob(selectedJob)}
                        >
                          <FileText className="h-4 w-4 mr-1" />
                          Auto-fill
                        </Button>
                        <Button
                          variant="secondary"
                          className="flex-1"
                          onClick={() => handleCopyToClipboard(selectedJob.description)}
                        >
                          <Copy className="h-4 w-4 mr-1" />
                          Copy
                        </Button>
                      </div>
                    </div>
                    </div>
                </ScrollArea>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
